import type { IslandPoint } from './settlement-island-registration';

export interface SettlementShoreFeatherBand {
  /** Distance from the preservation boundary in the caller's drawing coordinates. */
  radius: number;
  eraseAlpha: number;
}

const bandCount = 32;

/**
 * Outer-to-inner destination-out strokes multiply to a sampled smoothstep transmission.
 * Midpoint band boundaries keep quantization error below 1.5 / 64 (< 2.35%).
 * The innermost stroke erases fully, so a hard preservation clip cannot expose a seam.
 */
export function planSettlementShoreFeather(
  width = 16,
): readonly SettlementShoreFeatherBand[] | undefined {
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(width * 2)) return undefined;
  const bands: SettlementShoreFeatherBand[] = [];
  let previousTransmission = 1;
  for (let index = bandCount - 1; index >= 0; index--) {
    const t = index / bandCount;
    const transmission = t * t * (3 - 2 * t);
    bands.push({
      radius: ((index + 0.5) / bandCount) * width,
      eraseAlpha: 1 - transmission / previousTransmission,
    });
    previousTransmission = transmission;
  }
  return bands;
}

export type SettlementShoreFeatherCanvas = Pick<
  CanvasRenderingContext2D,
  | 'save'
  | 'restore'
  | 'beginPath'
  | 'moveTo'
  | 'lineTo'
  | 'closePath'
  | 'fill'
  | 'stroke'
  | 'setLineDash'
  | 'globalCompositeOperation'
  | 'globalAlpha'
  | 'filter'
  | 'shadowBlur'
  | 'shadowColor'
  | 'shadowOffsetX'
  | 'shadowOffsetY'
  | 'lineWidth'
  | 'lineJoin'
  | 'lineCap'
  | 'lineDashOffset'
  | 'strokeStyle'
  | 'fillStyle'
>;

/**
 * Erase the preserved interior and feather only its outside border on an expansion texture.
 * Pass the projected outline and width = 16 * cover.scale; the existing canvas transform
 * supplies texture resolution. Rounded strokes approximate distance to the closed polygon.
 * The caller's clip and drawing state survive; its current path is consumed. No pixel reads,
 * temporary textures or per-frame work are needed. The original basin is never drawn into.
 */
export function drawSettlementShoreFeather(
  context: SettlementShoreFeatherCanvas,
  outline: readonly IslandPoint[],
  width = 16,
): boolean {
  const bands = planSettlementShoreFeather(width);
  if (
    !bands ||
    outline.length < 3 ||
    !outline.every(({ x, y }) => Number.isFinite(x) && Number.isFinite(y))
  )
    return false;
  const origin = outline[0]!;
  const area = outline.reduce((sum, point, index) => {
    const next = outline[(index + 1) % outline.length]!;
    return (
      sum + (point.x - origin.x) * (next.y - origin.y) - (next.x - origin.x) * (point.y - origin.y)
    );
  }, 0);
  if (!Number.isFinite(area) || area === 0) return false;

  context.save();
  try {
    context.globalCompositeOperation = 'destination-out';
    context.globalAlpha = 1;
    context.filter = 'none';
    context.shadowBlur = 0;
    context.shadowColor = 'transparent';
    context.shadowOffsetX = 0;
    context.shadowOffsetY = 0;
    context.lineJoin = 'round';
    context.lineCap = 'round';
    context.setLineDash([]);
    context.lineDashOffset = 0;
    context.strokeStyle = '#000';
    context.fillStyle = '#000';
    context.beginPath();
    outline.forEach(({ x, y }, index) => {
      if (index === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    });
    context.closePath();
    context.fill();
    for (const band of bands) {
      context.globalAlpha = band.eraseAlpha;
      context.lineWidth = 2 * band.radius;
      context.stroke();
    }
  } finally {
    context.restore();
  }
  return true;
}
